import {
  CreateFeedbackNoteRequest,
  FEEDBACK_MAX_AGE_MS,
  FEEDBACK_QUEUE_MAX,
  FEEDBACK_QUEUE_MAX_BYTES,
} from "@edu/shared";
import { postFeedback } from "../../lib/api.ts";

/**
 * Очередь заметок о неточностях (раздел 6.5 `docs/feedback-notes-design.md`).
 *
 * Главное свойство фичи одно: **заметка не теряется**. Пишут её ровно тогда,
 * когда что-то сломалось, то есть отправка будет падать, и падать именно в этот
 * момент. Поэтому «Отправить» сначала пишет в `localStorage` и только потом
 * зовёт сеть: заметка существует до того, как о ней узнала сеть.
 *
 * **Модульный слой без React, и это требование, а не стиль.** Три следствия:
 *
 * 1. Цикл разбора обязан работать при ЛЮБОМ состоянии сессии. `lib/session.tsx`
 *    переводит вкладку в `anon` при любой неудаче `getMe()`, включая недоступный
 *    API. Живи цикл внутри условия «вошёл», при лежащем API он не завёлся бы
 *    вовсе, и очередь не разобралась бы никогда, пока API не поднимется И
 *    человек не перезагрузит вкладку.
 * 2. `session.tsx` зовёт `flushBeforeSignOut()` напрямую, из обоих мест выхода
 *    (`Shell` и `JoinPage`). React-контекст заметок стоит НИЖЕ провайдера
 *    сессии, то есть по направлению зависимости сессия про очередь ничего знать
 *    бы не могла.
 * 3. Стартовый чанк грузит ученик на 128 кбит/с. Здесь нет ни импорта React, ни
 *    единой новой зависимости.
 *
 * ⚠️ **Ключ `fb:<userId>:<noteId>`, и `userId` в нём обязателен.** Первая
 * причина как у черновиков домашки (`features/homework/drafts.ts`): за одним
 * ноутбуком меняются два человека. Вторая жёстче: `author_user_id` сервер берёт
 * ИЗ СЕССИИ, поэтому заметка, отправленная под чужой сессией, будет приписана не
 * тому автору. Порядок частей отличается от прецедента (`userId` вторым, а не
 * последним) намеренно: так очередь отбирается по префиксу.
 *
 * ⚠️ **Ничего не удаляется молча, никогда.** Переполнение отклоняет НОВУЮ
 * заметку с внятным сообщением, а не вытесняет старую: вытеснение это та же
 * молчаливая потеря, только отложенная. Потолок по возрасту не удаляет заметку,
 * а делает её видимой неудачей, готовой к копированию в мессенджер.
 */

/* ─────────────────────────── состояние слоя ─────────────────────────── */

const PREFIX = "fb:";

/**
 * Пауза между попытками: 60 с, 2 мин, 5 мин, 15 мин и дальше 15 мин.
 *
 * ⚠️ **Первый шаг это одновременно и ПОЛ, действующий независимо от повода**,
 * включая событие `online` и включая нажатие «Отправить». Сброса паузы по поводу
 * нет ни одного, потому что на мобильной сети `online` срабатывает при каждом
 * переключении вышки и при переходе Wi-Fi в LTE, то есть десятки раз за урок.
 * Сброс по нему вернул бы залповую отправку, которая глушит лимит частоты на
 * весь префикс сети (ручка держит 60 запросов в час на префикс `/24`), то есть
 * очередь мешала бы сама себе ровно в тех условиях, ради которых написана.
 *
 * ⚠️ **Счётчика попыток нет вовсе.** Он был в первой редакции проекта и оказался
 * ловушкой: заметка исчерпывала все попытки на `429`, который сама же и
 * создавала. Потолок один и он по возрасту, `FEEDBACK_MAX_AGE_MS`.
 */
const BACKOFF_MS = [60_000, 120_000, 300_000, 900_000];

/** Сколько байт готовы дослать на выгрузке вкладки, см. `sendOnUnload`. */
const KEEPALIVE_BUDGET = 60_000;

/** Возраст, после которого чужой ключ считается брошенным (раздел 6.5). */
const FOREIGN_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface Entry {
  key: string;
  noteId: string;
  clientTs: number;
  text: string;
  /**
   * Готовое тело запроса, либо `null`, если запись не разбирается схемой. Такая
   * запись показывается неудачей с её текстом: слать её некуда, но терять текст
   * нельзя, а показать его можно.
   */
  note: CreateFeedbackNoteRequest | null;
  failed: boolean;
  /** Вес записи в хранилище, для потолка `FEEDBACK_QUEUE_MAX_BYTES`. */
  bytes: number;
}

let started = false;
let currentUser: string | null = null;
/** Заметки, которые хранилище не приняло: живут ровно пока открыта вкладка. */
const memory = new Map<string, Entry>();
/** Сколько временных неудач подряд. Единственное, что двигает паузу. */
let strikes = 0;
let lastAttemptAt = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let active: Promise<void> | null = null;
/** Повод разобрать очередь, пришедший посреди круга. См. `pump`. */
let wake = false;
const listeners = new Set<() => void>();

/* ──────────────────────────── внешний вход ──────────────────────────── */

/** Одна заметка в списке неудач: показывается текстом целиком (раздел 7). */
export interface FailedNote {
  noteId: string;
  text: string;
  clientTs: number;
}

/**
 * Запускается один раз из `main.tsx`, БЕЗУСЛОВНО, при любом состоянии сессии
 * (разбор в шапке файла, пункт 1).
 */
export function startQueueLoop(): void {
  if (started) return;
  started = true;
  window.addEventListener("online", () => flushNow());
  // ⚠️ Слушателя `pagehide` здесь НЕТ намеренно: его держит `main.tsx` и зовёт
  // оттуда `flushBeforeSignOut()`. Второй слушатель на то же событие означал бы
  // два разбора очереди на одну выгрузку, то есть удвоенный залп по лимиту
  // частоты в самый неудачный момент
  flushNow();
}

/**
 * Кто сейчас в сессии. Зовётся из `lib/session.tsx` на каждое изменение.
 *
 * `null` не останавливает цикл, а лишь лишает его получателя: заметки лежат и
 * ждут следующего входа ЭТОГО ЖЕ человека. Отправить их под чужой сессией
 * нельзя, автора сервер берёт из сессии.
 */
export function setQueueUser(userId: string | null): void {
  if (userId === currentUser) return;
  currentUser = userId;
  // Пауза это свойство разговора с сервером, а не человека: новый вошедший не
  // должен ждать чужие пятнадцать минут. Залпа это не создаёт, потому что пол
  // считается от `lastAttemptAt`, а его не сбрасывает ни один повод
  strikes = 0;
  notify();
  if (userId !== null) {
    cleanupForeign(userId);
    flushNow();
  }
}

/**
 * Положить заметку в очередь. Возврат честно говорит, что произошло:
 *
 * - `queued` заметка в хранилище и переживёт закрытие вкладки;
 * - `memory` хранилище не пишет (приватный режим Safari бросает
 *   `QuotaExceededError` на первой же записи), заметка уедет, пока вкладка
 *   открыта, и человеку про это говорится В МОМЕНТ неудачи, а не заранее
 *   пугающей строкой в форме;
 * - `full` очередь переполнена, заметка НЕ принята, текст остаётся в поле.
 */
export function enqueue(
  userId: string,
  note: CreateFeedbackNoteRequest,
): "queued" | "memory" | "full" {
  const list = allEntries(userId);
  const raw = JSON.stringify({ note, failed: false });
  const used = list.reduce((sum, e) => sum + e.bytes, 0);
  // Потолки проверяются ДО записи и отклоняют новую заметку. Старые не
  // трогаются: вытеснение это отложенная молчаливая потеря
  if (list.length >= FEEDBACK_QUEUE_MAX) return "full";
  if (used + raw.length > FEEDBACK_QUEUE_MAX_BYTES) return "full";

  const stored = write(
    {
      key: keyOf(userId, note.noteId),
      noteId: note.noteId,
      clientTs: note.clientTs,
      text: note.text,
      note,
      failed: false,
      bytes: raw.length,
    },
    raw,
  );
  flushNow();
  return stored ? "queued" : "memory";
}

/**
 * Повод разобрать очередь: старт вкладки, `online`, вход, конец паузы.
 *
 * Ничего не форсирует: если с прошлой попытки прошло меньше положенного, ставит
 * таймер на остаток. Здесь и живёт пол в 60 секунд.
 */
export function flushNow(): void {
  void pump(false);
}

/**
 * Досылка «пока эта сессия ещё жива». Зовётся из ДВУХ мест, и ситуации разные:
 *
 * 1. `lib/session.tsx`, из `signOut()`, ДО сброса состояния и до
 *    `setQueueUser(null)`, то есть пока cookie ещё жива. Обычный клик внутри
 *    SPA: вкладка не выгружается, ответы дождутся, и результат нужен, чтобы
 *    предупредить человека, если дослать не вышло. Он уверен, что «Записал»
 *    означает «дойдёт», и молчать об этом нельзя.
 * 2. `main.tsx`, из слушателя `pagehide`. Вкладка выгружается.
 *
 * ⚠️ **Во втором случае отправка обязана идти с `keepalive: true`**, иначе
 * браузер обрежет запрос на выгрузке: строка кода выглядит работающей и не
 * доставляет ничего. Сигнатура общая на оба места и без аргументов, поэтому
 * выгрузку функция определяет сама, по `document.visibilityState`, а не по типу
 * события, которого она не видит. Прецедент рядом: `features/room/telemetry.ts`.
 *
 * ⚠️ Пауза здесь игнорируется, и это не дыра в правиле пола: это одно действие
 * человека, а не повод цикла, повторить его залпом нельзя, и альтернатива это
 * сказать «не дошло», даже не попытавшись.
 *
 * `true` означает «дошло всё, что могло дойти». Заметки с пометкой неудачи в
 * счёт не идут: ожиданием они не исправятся, они уже показаны в окне и ждут
 * кнопки «убрать».
 */
export async function flushBeforeSignOut(): Promise<boolean> {
  const userId = currentUser;
  if (userId === null) return true;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    sendOnUnload(userId);
    // Ответов мы не увидим, и предупреждать уже негде: вкладка закрывается.
    // Честный ответ это «в очереди осталось», а не выдуманное «всё хорошо»
    return queueStats(userId).pending === 0;
  }
  // Чужой круг, если он идёт, дожидаемся: заметка, написанная только что, могла
  // не попасть в уже начатый разбор, а ответ этой функции решает, показывать ли
  // человеку предупреждение перед выходом
  if (active !== null) await active;
  await pump(true);
  return allEntries(userId).every((e) => e.failed);
}

/** Заметки, которые сами уже не уедут. Показываются текстом целиком. */
export function listFailed(userId: string): FailedNote[] {
  return allEntries(userId)
    .filter((e) => e.failed)
    .map((e) => ({ noteId: e.noteId, text: e.text, clientTs: e.clientTs }));
}

/**
 * Убрать неудачу. Единственное место во всём слое, где заметка исчезает по
 * решению человека, и доступно оно только после того, как он увидел её текст
 * ЦЕЛИКОМ: иначе кнопка сама становится путём молчаливой потери.
 */
export function dropFailed(userId: string, noteId: string): void {
  remove(keyOf(userId, noteId));
}

/** Для точки на кнопке и для предупреждения при выходе. */
export function queueStats(userId: string): { pending: number; failed: number } {
  const list = allEntries(userId);
  return {
    pending: list.filter((e) => !e.failed).length,
    failed: list.filter((e) => e.failed).length,
  };
}

/** Подписка на изменения очереди. Возвращает отписку. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/* ───────────────────────────── цикл разбора ─────────────────────────── */

/**
 * Сколько ждать до следующей попытки. Ноль подряд идущих неудач это пол, то
 * есть первый же шаг: пауза не исчезает после удачи, она лишь возвращается к
 * минимальной.
 */
function delay(): number {
  const i = Math.min(Math.max(strikes - 1, 0), BACKOFF_MS.length - 1);
  return BACKOFF_MS[i] ?? 900_000;
}

/** Когда разрешена следующая попытка. Ноль означает «прямо сейчас». */
function dueAt(): number {
  return lastAttemptAt === 0 ? 0 : lastAttemptAt + delay();
}

/**
 * Разбор идёт в один поток: два круга одновременно устроили бы себе `429` и
 * могли бы отправить одну заметку дважды.
 *
 * ⚠️ **Повод, пришедший во время круга, не теряется, а отрабатывает сразу
 * после.** Без этого «Отправить», нажатое в момент, когда цикл уже разбирал
 * очередь, не давало бы НИ отправки, НИ таймера: круг заканчивался бы до того,
 * как узнал о новой заметке, а следующего повода могло не быть до перезагрузки
 * вкладки. Заметка при этом лежала бы в очереди целой, то есть поломка выглядела
 * бы как «отправилось, но не дошло», и искали бы её на сервере.
 */
function pump(force: boolean): Promise<void> {
  if (active !== null) {
    wake = true;
    return active;
  }
  if (!force) {
    const wait = dueAt() - Date.now();
    if (wait > 0) {
      schedule(wait);
      return Promise.resolve();
    }
  }
  const running = run().finally(() => {
    active = null;
    if (!wake) return;
    wake = false;
    // Без форсирования: пол в 60 секунд обязан пережить и этот путь тоже,
    // иначе он обходится частым нажатием
    flushNow();
  });
  active = running;
  return running;
}

function schedule(ms: number): void {
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void pump(false);
  }, Math.max(0, ms));
}

/**
 * Одна попытка разобрать очередь.
 *
 * ⚠️ **Заметки шлются ПОСЛЕДОВАТЕЛЬНО и по возрастанию `clientTs`.** Параллельно
 * нельзя: лимит частоты общий на префикс сети, и пачка запросов сама себе
 * устроила бы `429`. По возрасту, потому что порядок написанного это часть
 * смысла: три заметки об одной поломке читаются подряд.
 *
 * ⚠️ **Классификация строго по HTTP-СТАТУСУ, а не по `ApiError.code`.** Вся
 * конвенция проекта (типизированный `ErrorCode`, `switch` по коду на экранах)
 * толкает разбирать код, и там стоит известный непочиненный дефект: отказ по
 * лимиту частоты приезжает в браузер как `bad_request`, а не `rate_limited`
 * (`docs/engineering-standards.md`, раздел 5б). Разбор по коду отправил бы `429`
 * в ветку постоянных отказов, то есть помечал бы заметку безнадёжной на том
 * самом лимите, вокруг которого написана вся очередь.
 */
async function run(): Promise<void> {
  const userId = currentUser;
  // Нет сессии: слать некому, ждём входа. Это не остановка цикла, повод придёт
  // от `setQueueUser`
  if (userId === null) return;

  for (const entry of allEntries(userId).filter((e) => !e.failed)) {
    // Человек вышел или сменился прямо посреди разбора: остаток не наш
    if (currentUser !== userId) return;

    // Потолок один и он по возрасту. Заметка не удаляется, а становится видимой
    // и копируемой: асинхронный урок может идти часами без единого удачного
    // запроса, и потолок в попытках убил бы именно такие заметки
    if (entry.note === null || Date.now() - entry.clientTs > FEEDBACK_MAX_AGE_MS) {
      markFailed(entry);
      continue;
    }

    lastAttemptAt = Date.now();
    const status = await postFeedback(entry.note);

    // 202 это принято, В ТОМ ЧИСЛЕ повтор: ключ идемпотентности на сервере
    // отвечает так же, потому что повтор здесь спроектированный штатный путь
    if (status === 202) {
      remove(entry.key);
      strikes = 0;
      continue;
    }

    // Постоянные. 400 это «такое тело сервер не примет никогда», 403 это
    // заблокированный аккаунт (единственный намеренный 403 в проекте, и раздел
    // 5.1 проекта прямо называет блокировку ограничителем против спам-заметок).
    // Постоянная неудача цикл НЕ останавливает: она помечает свою заметку, и
    // разбор идёт дальше, иначе одна битая заметка держала бы всю очередь
    if (status === 400 || status === 403) {
      markFailed(entry);
      continue;
    }

    /*
      Временные, и цикл останавливается на первой же:

      401 сессия истекла или отозвана. Именно временная: человек войдёт снова,
          и заметка уедет. Запрос уходит с `silent401`, иначе фоновый таймер
          выкидывал бы человека на форму входа посреди урока.
      404 маршрута ЕЩЁ нет. Выглядит постоянным, но это окно выкатки: скрипт
          заливает бандл веба раньше, чем поднимает API, и между этими шагами
          новый бандл обслуживается старым API. Постоянная пометка означала бы,
          что каждая выкатка убивает все заметки, написанные в эту минуту.
      429 лимит частоты. Ровно то, ради чего пауза и растёт.
      5xx перезапуск API это штатное регулярное событие (раздел 9 CLAUDE.md).
      0   до сервера не дошло: обрыв сети, тот самый случай, про который пишут.
    */
    strikes += 1;
    schedule(delay());
    notify();
    return;
  }

  // Разобрали до конца: пауза больше ничего не сторожит
  strikes = 0;
  notify();
}

/**
 * Залп на выгрузке вкладки. Ответы не читаются, их некому дождаться, поэтому
 * записи остаются на месте: если запрос дошёл, следующая попытка получит `202`
 * по ключу идемпотентности, и задвоения не будет.
 *
 * ⚠️ **Именно залп, а не последовательный цикл.** Обычный разбор ждёт ответа
 * перед следующим запросом, а на выгружаемой вкладке продолжения после `await`
 * может не случиться вовсе: доедет первая заметка, остальные не будут даже
 * отправлены. С `keepalive` браузер доносит уже выданные запросы сам, значит
 * выдать их надо все сразу.
 *
 * Бюджет по байтам стоит потому, что браузер держит общий потолок на все
 * `keepalive`-запросы выгружаемой вкладки и превышение отменяет их МОЛЧА, то
 * есть без бюджета можно потерять и то, что влезало.
 */
function sendOnUnload(userId: string): void {
  let budget = KEEPALIVE_BUDGET;
  for (const entry of allEntries(userId)) {
    if (entry.failed || entry.note === null) continue;
    if (budget - entry.bytes < 0) return;
    budget -= entry.bytes;
    void postFeedback(entry.note, { keepalive: true });
  }
}

/* ────────────────────────────── хранилище ───────────────────────────── */

function keyOf(userId: string, noteId: string): string {
  return `${PREFIX}${userId}:${noteId}`;
}

/**
 * Все записи человека, по возрастанию `clientTs`. Читает хранилище и память
 * вкладки: заметка, не принятая хранилищем, участвует во всём наравне.
 *
 * ⚠️ Мусорные ключи собираются в список и удаляются ПОСЛЕ обхода. Удаление
 * внутри обхода по индексу сдвигает нумерацию `localStorage`, то есть молча
 * пропускает соседнюю запись, а соседняя запись это чья-то заметка. Тот же
 * приём и по той же причине в `cleanupDrafts` (`features/homework/drafts.ts`).
 */
function allEntries(userId: string): Entry[] {
  const out: Entry[] = [];
  const seen = new Set<string>();
  const garbage: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === null || !key.startsWith(PREFIX)) continue;
      const parts = key.split(":");
      const noteId = parts[2];
      if (parts[1] !== userId || noteId === undefined) continue;
      const raw = localStorage.getItem(key);
      if (raw === null) continue;
      const entry = parseEntry(key, noteId, raw);
      // Ключ, из которого не достать даже текста: спасать нечего. Это уборка
      // мусора, а не потеря заметки, поэтому она и разрешена
      if (entry === null) {
        garbage.push(key);
        continue;
      }
      out.push(entry);
      seen.add(key);
    }
  } catch {
    /* хранилище не читается: остаётся память вкладки, она ниже */
  }
  for (const key of garbage) remove(key);
  for (const [key, entry] of memory) {
    if (key.startsWith(`${PREFIX}${userId}:`) && !seen.has(key)) out.push(entry);
  }
  return out.sort((a, b) => a.clientTs - b.clientTs);
}

/**
 * Разбор записи. Мягкий намеренно: запись, не прошедшую схему (старый бандл,
 * недописанный `setItem`, чужая правка), мы не выбрасываем, а показываем
 * неудачей с её текстом. Строгий разбор превращал бы её в тихую потерю ровно
 * того, что фича обещает не терять.
 */
function parseEntry(key: string, noteId: string, raw: string): Entry | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) return null;
  const box = data as { note?: unknown; failed?: unknown };
  const parsed = CreateFeedbackNoteRequest.safeParse(box.note);
  if (parsed.success) {
    return {
      key,
      noteId,
      clientTs: parsed.data.clientTs,
      text: parsed.data.text,
      note: parsed.data,
      failed: box.failed === true,
      bytes: raw.length,
    };
  }
  const stray = (typeof box.note === "object" && box.note !== null ? box.note : {}) as {
    text?: unknown;
    clientTs?: unknown;
  };
  if (typeof stray.text !== "string") return null;
  return {
    key,
    noteId,
    clientTs: typeof stray.clientTs === "number" ? stray.clientTs : 0,
    text: stray.text,
    note: null,
    failed: true,
    bytes: raw.length,
  };
}

/**
 * `false` означает «хранилище не пишет»: запись остаётся в памяти вкладки.
 *
 * Приватный режим Safari бросает на первой же записи, то есть это штатный
 * браузер, а не экзотика. Заметка при этом не пропадает, она становится
 * короткоживущей, и `enqueue` возвращает это наружу словом.
 */
function write(entry: Entry, raw?: string): boolean {
  const body = raw ?? JSON.stringify({ note: entry.note, failed: entry.failed });
  entry.bytes = body.length;
  try {
    localStorage.setItem(entry.key, body);
    memory.delete(entry.key);
    notify();
    return true;
  } catch {
    memory.set(entry.key, entry);
    notify();
    return false;
  }
}

function markFailed(entry: Entry): void {
  entry.failed = true;
  write(entry);
}

function remove(key: string): void {
  memory.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {
    /* хранилище не пишет: удалять там было нечего */
  }
  notify();
}

/**
 * Уборка чужих ключей.
 *
 * ⚠️ **Оба условия сразу: ключ чужой И старше тридцати суток.** Одного возраста
 * мало: свежая чужая заметка ждёт своего хозяина, и стереть её значит потерять
 * чужую работу. Одной чуждости мало: человек может вернуться в свой аккаунт
 * через час, а из текущей сессии его ключи выглядят чужими.
 *
 * Без уборки такие ключи не удаляются НИЧЕМ: виджету текущего пользователя они
 * невидимы, а пометка неудачи сама не исчезает. Прецедент и та же причина:
 * `cleanupDrafts` в `features/homework/drafts.ts`.
 */
function cleanupForeign(userId: string): void {
  try {
    const doomed: string[] = [];
    const now = Date.now();
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === null || !key.startsWith(PREFIX)) continue;
      const parts = key.split(":");
      const noteId = parts[2];
      if (parts[1] === userId || parts[1] === undefined || noteId === undefined) continue;
      const raw = localStorage.getItem(key);
      if (raw === null) continue;
      const entry = parseEntry(key, noteId, raw);
      if (entry === null || now - entry.clientTs > FOREIGN_MAX_AGE_MS) doomed.push(key);
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    /* хранилище не читается: чистить нечего */
  }
}

function notify(): void {
  for (const fn of listeners) fn();
}
